// src/components/ChatBox.jsx
import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Paperclip, X, Image, FileAudio } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../api';

const API_URL = process.env.REACT_APP_API_URL || '/';

export default function ChatBox({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('authToken');

  // --- اسکرول خودکار ---
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isTyping]);

  // --- شروع چت ---
  useEffect(() => {
    const startChat = async () => {
      if (!token) return;

      try {
        const res = await api.post('/api/chat/start', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoomId(res.data.room_id);

        socketRef.current = io(API_URL, {
          auth: { token },
          transports: ['polling', 'websocket'],
          path: '/socket.io'
        });

        const socket = socketRef.current;

        socket.emit('join_room', { room_id: res.data.room_id });

        socket.on('previous_messages', (msgs) => {
          setMessages(msgs.map(m => ({ ...m, animate: false })));
        });

        socket.on('receive_message', (msg) => {
          setMessages(prev => {
            const exists = prev.some(m => 
              m.id === msg.id || 
              (m.message_text === msg.message_text && 
               m.file_url === msg.file_url && 
               Math.abs(new Date(m.created_at) - new Date(msg.created_at)) < 1000)
            );
            if (exists) return prev;
            return [...prev, { ...msg, animate: true }];
          });
          setIsTyping(false);
        });

        socket.on('typing', () => setIsTyping(true));
        socket.on('stop_typing', () => setIsTyping(false));

        socket.on('connect', () => console.log('چت وصل شد'));
        socket.on('connect_error', () => {
          setError('اتصال به چت قطع شد');
          setTimeout(() => setError(''), 3000);
        });

      } catch (err) {
        setError('خطا در شروع چت');
        setTimeout(() => setError(''), 3000);
        onClose?.();
      }
    };

    startChat();

    return () => {
      socketRef.current?.disconnect();
    };
  }, [token, onClose]);

  // --- تشخیص تایپ ---
  useEffect(() => {
    if (!message.trim() || !roomId || !socketRef.current) return;
  
    socketRef.current.emit('typing', { room_id: roomId });
    const timeout = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { room_id: roomId });
    }, 1000);
  
    return () => clearTimeout(timeout);
  }, [message, roomId]);
  
  // --- ارسال پیام ---
  const sendMessage = async () => {
    if (!message.trim() && !file) return;

    let file_url = null;
    let file_type = null;

    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await api.post('/api/chat/upload', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        file_url = res.data.file_url;
        file_type = res.data.file_type;
        setSuccess('فایل آپلود شد');
        setTimeout(() => setSuccess(''), 2000);
      } catch (err) {
        setError('خطا در آپلود فایل');
        setTimeout(() => setError(''), 3000);
        return;
      }
    }

    socketRef.current?.emit('send_message', {
      room_id: roomId,
      message: message.trim(),
      file_url,
      file_type,
    });

    setMessage('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- پیش‌نمایش فایل ---
  const FilePreview = ({ file }) => {
    if (!file) return null;
    const isImage = file.type.startsWith('image/');

    return (
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex items-center gap-2 bg-gray-100 rounded-xl p-2 text-xs mb-2"
      >
        {isImage ? <Image className="w-4 h-4 text-indigo-600" /> : <FileAudio className="w-4 h-4 text-purple-600" />}
        <span className="truncate max-w-[120px]">{file.name}</span>
        <button onClick={() => setFile(null)} className="text-red-500 hover:text-red-700">
          <X className="w-4 h-4" />
        </button>
      </motion.div>
    );
  };

  // --- پیام‌ها (اصلی‌ترین تغییر) ---
  const MessageBubble = ({ msg }) => {
    const isUser = !msg.is_admin; // کاربر عادی = راست، ادمین = چپ
    const fileUrl = msg.file_url?.startsWith('/') ? msg.file_url : `${API_URL}${msg.file_url}`;
  
    return (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className={`flex mb-3 ${isUser ?'justify-start' :  'justify-end' }`}
      >
        <div
          className={`max-w-[75%] px-4 py-3 rounded-2xl shadow-lg break-words relative ${
            isUser
              ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
              : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
          }`}
        >
          {/* مثلث جهت‌دهی */}
          <div
            className={`absolute top-3 w-0 h-0 border-8 border-transparent ${
              isUser
                ? 'right-[-8px] border-l-emerald-500'
                : 'left-[-8px] border-r-indigo-500'
            }`}
          />
  
          {msg.message_text && (
            <p className="text-sm leading-relaxed font-medium">{msg.message_text}</p>
          )}
  
          {msg.file_url && (
            <div className="mt-3">
              {msg.file_type === 'image' ? (
                <img
                  src={fileUrl}
                  alt="آپلود شده"
                  className="max-w-full rounded-xl cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => window.open(fileUrl, '_blank')}
                />
              ) : (
                <audio controls src={fileUrl} className="w-full rounded-xl shadow-sm" />
              )}
            </div>
          )}
  
          <div className="text-xs mt-1 opacity-90 text-right">
            {new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </motion.div>
    );
  };

  // --- تایپینگ ---
  const TypingIndicator = () => (
    <div className="flex justify-start mb-3">
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-4 py-3 rounded-2xl shadow-lg">
        <div className="flex gap-1">
          <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-2 h-2 bg-white rounded-full" />
          <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.1 }} className="w-2 h-2 bg-white rounded-full" />
          <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-2 h-2 bg-white rounded-full" />
        </div>
      </div>
    </div>
  );

  // --- پیام‌ها ---
  const Toast = ({ type, message }) => (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      className={`fixed top-16 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-2 ${
        type === 'error' ? 'bg-red-500/90 text-white' : 'bg-green-500/90 text-white'
      } backdrop-blur-lg`}
    >
      <span className="font-medium text-sm">{message}</span>
    </motion.div>
  );

  return (
    <div className="flex flex-col h-full">

      {/* --- لیست پیام‌ها --- */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'env(safe-area-inset-bottom, 1rem)'
        }}
      >
        <div className="min-h-full flex flex-col justify-end">
          {messages.map((msg) => (
            <MessageBubble key={`${msg.id}-${msg.created_at}`} msg={msg} />
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} className="h-1" />
        </div>
      </div>

      {/* --- ورودی پیام --- */}
      <div 
        className="bg-white/80 backdrop-blur-xl border-t border-white/30 p-4"
        style={{ 
          paddingBottom: 'max(1rem, env(safe-area-inset-bottom))'
        }}
      >
        <FilePreview file={file} />
        
        <div className="flex gap-2 items-center mt-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="پیام خود را بنویسید..."
            className="flex-1 px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-full focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all text-sm"
          />
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,audio/*"
            onChange={(e) => setFile(e.target.files[0])}
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current.click()}
            className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full text-indigo-600 shadow-md hover:shadow-lg transition-all"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          <button
            onClick={sendMessage}
            disabled={!message.trim() && !file}
            className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* --- پیام‌ها --- */}
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4 pointer-events-none">
        <AnimatePresence>
          {error && <Toast type="error" message={error} />}
          {success && <Toast type="success" message={success} />}
        </AnimatePresence>
      </div>
    </div>
  );
}